// Fictional chat responses for ?headings_mock=1 on an existing v2 chat.
const sources = [
  {
    type: 'url',
    name: 'Northwind Mobile · Investor update',
    title: 'Network expansion and operating priorities',
    summary: 'Sample investor update covering network growth and operating efficiency.',
    url: 'https://example.com/northwind/investor-update',
  },
  {
    type: 'url',
    name: 'Northwind Mobile · Leadership interview',
    title: 'Building a simpler customer experience',
    summary: 'Sample leadership interview about service quality and operational complexity.',
    url: 'https://example.com/northwind/leadership-interview',
  },
  {
    type: 'url',
    name: 'Northwind Mobile · Account plan',
    title: 'Renewal and expansion opportunities',
    summary: 'Sample account plan with milestones, stakeholders, and success measures.',
    url: 'https://example.com/northwind/account-plan',
  },
]

export const HEADINGS_MOCKS = [
  {
    id: 'account-brief',
    title: 'Create an account brief for Northwind Mobile',
    sources,
    text: `Demo account brief for a fictional company. All figures and sources are sample data.

# Primary-source coverage

The brief combines an investor update, a leadership interview, and an account plan. [Investor update + 2](sources://1,2,3)

- **Business performance:** network expansion and customer retention.
- **Leadership priorities:** simpler operations and a consistent service experience.
- **Account context:** an existing relationship with room to expand.

# 1. Executive summary

- **Growth is creating operational pressure.** Northwind is adding customers while managing an expanding network. [Investor update](sources://1)
- **The strongest opening is service efficiency.** Connect lower support costs with faster incident resolution.
- **Build on the current relationship.** Start with a narrow pilot and expand after the team proves the outcome. [Account plan](sources://3)

**Urgency:** the renewal review is approaching, and the team wants a measurable improvement before the next planning cycle.

**Account posture:** expanding across existing business units.

# 2. Company snapshot

### Illustrative operating metrics

| Metric | Current | Target |
| --- | --- | --- |
| Active customers | 2.4 million | 2.8 million |
| First-contact resolution | 68% | 78% |
| Average handling time | 11 minutes | 8 minutes |

## What the numbers suggest

The service organization needs to absorb growth without adding the same proportion of support effort.

# 3. What changed since the previous review

1. A regional network rollout increased the number of supported locations.
2. Support leaders introduced a shared incident workflow.
3. Finance requested a clearer link between tool spending and service outcomes.

The next discussion should connect these changes to a single pilot with an agreed owner. [Investor update + 1](sources://1,3)

# 4. Leadership voice

> We want our teams to spend less time moving information between systems and more time solving the customer problem.

This fictional interview excerpt points to workflow simplification as a useful conversation starter. [Leadership interview](sources://2)

# 5. Strategic priorities and next steps

- Agree on a service workflow for the pilot.
- Confirm the operations sponsor and the technical owner.
- Record a baseline for resolution time and repeat contacts.
- Schedule a results review before the renewal decision.

**Suggested opener:** Where does the team lose the most time between identifying an issue and resolving it?`,
  },
  {
    id: 'nested-markdown',
    title: 'Show a technical rollout plan with nested headings and code examples',
    text: `# 1. Rollout plan

Everything in this plan belongs to the first section until the next top-level heading.

## Preparation

Confirm access, record the baseline, and choose a small pilot group.

### Owners

- Operations owns the rollout.
- Engineering owns the integration.
  - Support reviews the escalation path.

#### Acceptance criteria

The pilot must complete a full workflow without manual data entry.

### Configuration example

\`\`\`json
{
  "pilot": true,
  "region": "north",
  "notifications": ["operations", "support"]
}
\`\`\`

### Markdown example

\`\`\`markdown
# This is example code, not another collapsible section
## Neither is this heading
\`\`\`

> # A quoted heading
>
> This heading stays inside the rollout section.

- A list item with its own heading:

  # A nested list heading

  This content stays inside the same list item.

# 2. Validation

### Pilot checklist

| Check | Owner | Expected result |
| --- | --- | --- |
| Authentication | Engineering | Pilot users can sign in |
| Workflow | Operations | No manual handoff |
| Escalation | Support | Correct team notified |

After the checklist passes, schedule the next rollout group.`,
  },
  {
    id: 'formatted-headings',
    title: 'Show headings with formatting, citations, and a long title',
    sources,
    text: `A short introduction stays above the collapsible sections.

# **Expansion** opportunities and *customer priorities* [Account plan](sources://3)

Formatting and source links remain interactive inside the heading.

The pilot should address one measurable customer problem. [Investor update + 1](sources://1,2)

# A longer heading about aligning operations, engineering, customer experience, and commercial stakeholders before the next renewal review

This section provides a long title for checking wrapping on smaller screens.

1. Agree on the outcome.
2. Name an accountable owner.
3. Set the review date.

# Launch checklist for \`renewal_v2\`

**Owner:** Alex Morgan  
**Status:** ready for review

Contact [the demo team](mailto:team@example.com) or read the [sample plan](https://example.com/northwind/account-plan).`,
  },
  {
    id: 'repeated-empty-headings',
    title: 'Show repeated titles and sections with no content',
    text: `# Next steps

# Next steps

The first section is empty. This paragraph belongs to the second section with the same title.

# Risks

The rollout owner still needs to confirm the review date.

# Pending details`,
  },
  {
    id: 'single-section',
    title: 'Give me a short meeting preparation note',
    text: `# Meeting preparation

**Goal:** agree on a pilot with a clear owner and success measure.

- Ask where service handoffs slow the team down.
- Confirm which workflow has enough activity to measure progress.
- End with an owner, a date, and one next action.

## Suggested opening question

What would need to improve in the next month for this pilot to feel useful?`,
  },
  {
    id: 'no-top-level-heading',
    title: 'Give me a plain answer without top-level headings',
    text: `This answer has no top-level headings and should remain regular Markdown.

## Summary

Start with a small pilot, measure the result, and review it with the team.

### Suggested actions

- Confirm the owner.
- Record the baseline.
- Schedule a review.

\`\`\`markdown
# A heading inside a code block stays code
\`\`\`

> # A heading inside a quote stays in the quote
>
> No collapsible section is created here.`,
  },
  {
    id: 'alternate-heading-syntax',
    title: 'Show underlined Markdown headings and an HTML heading',
    text: `A short introduction before the first section.

Underlined Markdown heading
===========================

An equals underline creates an H1 and starts a collapsible section.

Nested underlined heading
-------------------------

A dash underline creates an H2 that remains inside the current section.

<h1 id="demo-html-heading">HTML heading with an anchor</h1>

This paragraph belongs to the HTML heading. Its anchor remains available.

# Final Markdown heading

This is the final paragraph of the final section.`,
  },
  {
    id: 'long-report',
    title: 'Create a longer report with many collapsible sections',
    text: `Illustrative quarterly review for Northwind Mobile. All details below are fictional.

${[
    'Business overview',
    'Customer growth',
    'Service experience',
    'Network operations',
    'Product adoption',
    'Partner ecosystem',
    'Commercial priorities',
    'Delivery readiness',
    'Team ownership',
    'Open questions',
    'Decision timeline',
    'Recommended next steps',
  ].map((title, index) => `# ${index + 1}. ${title}

The team is reviewing ${title.toLowerCase()} as part of the next planning cycle. The goal is to connect current activity with a measurable customer or business outcome.

## Discussion points

- What is working well today?
- Where does the current process create extra work?
- Who can confirm the baseline and own the next step?

**Next action:** capture the decision, name an owner, and agree on a review date.`).join('\n\n')}`,
  },
]

export const HEADINGS_MOCK_HISTORY = HEADINGS_MOCKS.flatMap(mock => [
  {
    type: 'user',
    data: {text: mock.title, title: mock.title},
  },
  {
    type: 'assistant',
    data: {text: mock.text, sources: mock.sources || []},
  },
])
